/* eslint-disable lingui/no-unlocalized-strings -- caminhos e rótulos técnicos de log, não texto de UI */
import type { z } from 'zod';

import { billingApi } from '@/shared/http';

import {
  billingSettingListQuery,
  billingSettingOptionPageSchema,
  billingSettingOptionSchema,
  billingSettingPageSchema,
  type BillingSettingBody,
  type BillingSettingListParams,
} from './billing-setting.wire';

// Cada função cita o serviço (e o hook que monta a query) do legado em UYZY@e7f4d7b25. Legenda:
// docs/discovery/modulos/billing-setting.md §6, coluna #. Timeout padrão (16 s) em todas.

/** U27: fio fora do contrato só avisa fora de produção e segue com o valor cru. */
function wire<S extends z.ZodType>(schema: S, data: unknown, label: string): z.output<S> {
  const parsed = schema.safeParse(data);
  if (parsed.success) return parsed.data;
  if (!import.meta.env.PROD) console.warn(`${label} fora do contrato`, parsed.error.issues);
  return data as z.output<S>;
}

const withSignal = (signal: AbortSignal | undefined) => (signal ? { signal } : {});

/**
 * #1 `GET /BillingSetting` — `services/walletManagement/billingSetting/index.tsx:6-13`; query de
 * `contexts/wallet/billingParameters/billingParametersContext.tsx:37-39` (`{ ...filtros, page, size, tenant }`).
 */
export async function listBillingSettings(params: BillingSettingListParams, signal?: AbortSignal) {
  const data = await billingApi.get<unknown>('/BillingSetting', { params: billingSettingListQuery(params), ...withSignal(signal) });
  return wire(billingSettingPageSchema, data, 'GET /BillingSetting');
}

/** #2 `POST /BillingSetting` — `billingSetting/index.tsx:15-25` (sem id: "cria"). */
export async function createBillingSetting(body: BillingSettingBody): Promise<unknown> {
  return billingApi.post('/BillingSetting', body);
}

/** #3 `PUT /BillingSetting/{id}` — `billingSetting/index.tsx:15-25` (`isCreating` = tem id = edição). */
export async function updateBillingSetting(id: string, body: BillingSettingBody): Promise<unknown> {
  return billingApi.put<unknown>(`/BillingSetting/${id}`, body);
}

/** #4 `DELETE /BillingSetting/{id}` — `billingSetting/index.tsx:35-41`. */
export async function deleteBillingSetting(id: string): Promise<unknown> {
  return billingApi.delete<unknown>(`/BillingSetting/${id}`);
}

/**
 * #6 `GET /BillingSetting` como opções de autocomplete (porta F0 para `fund`, `collection`, Nova
 * cobrança) — `billingParametersContext.tsx:81-86`: `{ searchString, page: 0, size: 10, walletsCode, tenant }`.
 */
export async function listBillingSettingOptions(
  options: { searchString?: string | undefined; walletsCode?: string | number | null | undefined; tenant: string | undefined },
  signal?: AbortSignal,
) {
  const params = { searchString: options.searchString, page: 0, size: 10, walletsCode: options.walletsCode ?? undefined, tenant: options.tenant };
  const data = await billingApi.get<unknown>('/BillingSetting', { params, ...withSignal(signal) });
  return wire(billingSettingOptionPageSchema, data, 'GET /BillingSetting (opções)');
}

/** #7 `GET /BillingSetting/{id}` — `billingSetting/index.tsx:27-33` (a tela não usa; é da porta). */
export async function getBillingSetting(id: string, signal?: AbortSignal) {
  const data = await billingApi.get<unknown>(`/BillingSetting/${id}`, withSignal(signal));
  return wire(billingSettingOptionSchema, data, 'GET /BillingSetting/{id}');
}
